import { useCallback, type ReactNode } from 'react';

import { Badge, type BadgeTone } from '@/shared/ui/badge';
import { StatCard } from '@/shared/ui/stat-card';
import { dashboardApi } from '@/modules/dashboard/services/api';
import type { TenantSummary } from '@/modules/dashboard/services/summaries';
import { DashboardWidgetFrame, type DashboardWidgetProps } from './widget-frame';

/**
 * The platform's TENANT REGISTRY.
 *
 * ---------------------------------------------------------------------------
 * A PLATFORM-SCOPED READ
 * ---------------------------------------------------------------------------
 * `GET /api/v1/tenants` lists every tenant of the deployment, so it needs the `tenant.read`
 * platform grant; a tenant member receives `403`. The dashboard renders this widget only for an
 * actor who holds that grant — the frame still renders the shared refused state if the server
 * says no, because a grant can be revoked between the session being resolved and the read
 * being made.
 *
 * ---------------------------------------------------------------------------
 * "NO LEGAL ENTITY" IS LABELLED EXACTLY THAT
 * ---------------------------------------------------------------------------
 * A tenant with no legal entity recorded is counted as one. The widget does NOT call it "needs
 * setup" or "incomplete": a legal entity is a fact the platform holds, not a task the tenant is
 * behind on, and calling it a setup task invents a workflow that does not exist.
 */
const STATUS_TONE: Record<string, BadgeTone> = {
  active: 'positive',
  suspended: 'caution',
  closed: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  active: 'Active',
  suspended: 'Suspended',
  closed: 'Closed',
};

/** How many of the newest tenants the widget lists. */
const NEWEST_LIMIT = 5;

/** The tenants the widget lists. */
export type NewestTenantsProps = {
  readonly tenants: TenantSummary['tenants'];
};

/**
 * The newest tenants, newest first.
 *
 * The list is SORTED BY CREATION, not by the order the API returned: the API's order is not a
 * statement about which tenant is newest, and a registry whose first row moves between reads
 * reads as noise.
 */
function NewestTenants({ tenants }: NewestTenantsProps): ReactNode {
  const newest = [...tenants]
    .sort((left, right) => right.createdAt.localeCompare(left.createdAt))
    .slice(0, NEWEST_LIMIT);

  return (
    <div>
      <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-content-muted">
        Newest tenants
      </h3>
      <ul className="flex flex-col">
        {newest.map((tenant) => (
          <li
            key={tenant.id}
            className="flex items-center justify-between gap-3 border-b border-border py-2 text-sm last:border-b-0"
          >
            <span className="text-content">{tenant.slug}</span>
            <Badge tone={STATUS_TONE[tenant.status] ?? 'neutral'}>
              {STATUS_LABEL[tenant.status] ?? tenant.status}
            </Badge>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TenantRegistry({ className }: DashboardWidgetProps): ReactNode {
  const load = useCallback(() => dashboardApi.tenants(), []);

  return (
    <DashboardWidgetFrame
      title="Tenants"
      load={load}
      className={className}
      isEmpty={(data) => data.total === 0}
      emptyTitle="No tenants"
      emptyDescription="This deployment has no tenants yet."
    >
      {(data) => (
        <div className="flex flex-col gap-3">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <StatCard
              label="Tenants"
              value={String(data.total)}
              caption="On this deployment"
            />
            {Object.entries(data.byStatus).map(([status, count]) => (
              <StatCard
                key={status}
                label={STATUS_LABEL[status] ?? status}
                value={String(count)}
                caption={`${data.total} tenant${data.total === 1 ? '' : 's'} in total`}
              />
            ))}
            <StatCard
              label="No legal entity"
              value={String(data.withoutLegalEntity)}
              tone={data.withoutLegalEntity === 0 ? 'neutral' : 'warning'}
              caption="No legal entity recorded"
            />
          </div>

          <NewestTenants tenants={data.tenants} />

          <a
            href="/platform/tenants"
            className="text-sm font-medium text-accent underline-offset-4 hover:underline"
          >
            Open the tenant registry
          </a>
        </div>
      )}
    </DashboardWidgetFrame>
  );
}
